import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { Download, Upload, Plus, ShieldPlus } from "lucide-react";

const KINDS = [
  { key: "vehicle_models", label: "Vehicle Models" },
  { key: "accessories", label: "Accessories" },
  { key: "extended_warranties", label: "Extended Warranties" },
];

export function BulkUploadPanel() {
  const [kind, setKind] = useState("vehicle_models");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const download = async () => {
    const resp = await api.get(`/dealership/bulk/templates/${kind}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a"); a.href = url; a.download = `${kind}_template.csv`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const kindPath = kind === "vehicle_models" ? "vehicle-models" : kind === "accessories" ? "accessories" : "extended-warranties";
      const r = await api.post(`/dealership/bulk/${kindPath}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Created ${r.data.created} rows · skipped/failed ${r.data.skipped_or_failed}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="card-sharp p-4 space-y-3" data-testid="bulk-upload-panel">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Bulk upload</div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="rounded-[2px] w-56 mt-1" data-testid="bulk-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={download} variant="outline" className="rounded-[2px] gap-1" data-testid="bulk-download-tpl"><Download className="w-3.5 h-3.5" /> Sample CSV</Button>
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" id="bulk-upload-file" data-testid="bulk-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-[2px] bg-accent hover:bg-accent/90 text-white gap-1" data-testid="bulk-upload-btn"><Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload CSV"}</Button>
      </div>
      {result && (
        <div className="border rounded-[2px] p-3 bg-muted/30 text-xs" data-testid="bulk-result">
          <div className="font-mono">Created <span className="text-emerald-600">{result.created}</span> · Skipped/failed <span className="text-amber-500">{result.skipped_or_failed}</span></div>
          {result.errors?.length > 0 && (
            <div className="mt-2 max-h-40 overflow-auto">
              <div className="text-red-500 font-mono uppercase text-[10px] mb-1">Errors</div>
              {result.errors.slice(0, 20).map((e, i) => (
                <div key={i} className="text-[11px] font-mono">Row {e.row}: {e.error}</div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground italic">
        CSV format: download the template to see all columns. Pipe-separated multi-values (e.g., colours "Red|White|Blue"; applicable_models "Nexon·XZ+ Diesel|Harrier·XZA+ Dark").
      </div>
    </div>
  );
}

export function ExtendedWarrantyPanel() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { name: "", applicable_model_id: "", applicable_variant: "", duration_months: 24, price: 0, hsn_code: "9971", gst_rate: 18 };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [w, m] = await Promise.all([api.get("/dealership/extended-warranties"), api.get("/dealership/vehicle-models")]);
    setRows(w.data); setModels(m.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || form.price <= 0) return toast.error("Name and price required");
    try { await api.post("/dealership/extended-warranties", form); toast.success("Warranty added"); setOpen(false); setForm(emptyForm); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const modelName = (id) => { const m = models.find((x) => x.id === id); return m ? `${m.name} · ${m.variant}` : "All models"; };

  return (
    <div className="space-y-3" data-testid="ew-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Extended warranty catalog</div>
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-[2px] bg-accent hover:bg-accent/90 text-white gap-1" data-testid="new-ew-btn"><ShieldPlus className="w-3.5 h-3.5" /> New Warranty</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Name</th><th>Applies to</th><th className="text-right">Duration</th><th className="text-right">Price</th><th className="text-right">GST</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-muted-foreground">No warranties configured.</td></tr>}
            {rows.map((w) => (
              <tr key={w.id} data-testid={`ew-row-${w.name}`}>
                <td className="text-xs font-medium">{w.name}</td>
                <td className="text-xs">{modelName(w.applicable_model_id)}</td>
                <td className="text-right tabular-nums text-xs">{w.duration_months} m</td>
                <td className="text-right tabular-nums">{fmtINR(w.price)}</td>
                <td className="text-right tabular-nums text-xs">{w.gst_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">New Extended Warranty</SheetTitle>
            <SheetDescription className="sr-only">Add a warranty product applicable to one or all vehicle models.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-[2px] mt-1" data-testid="ew-name" /></div>
            <div>
              <Label>Applicable model (leave blank for all)</Label>
              <Select value={form.applicable_model_id} onValueChange={(v) => setForm({ ...form, applicable_model_id: v })}>
                <SelectTrigger className="rounded-[2px] mt-1" data-testid="ew-model"><SelectValue placeholder="All models" /></SelectTrigger>
                <SelectContent>{models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.variant}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duration (months)</Label><Input type="number" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: Number(e.target.value) })} className="rounded-[2px] mt-1 font-mono" /></div>
              <div><Label>Price *</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="rounded-[2px] mt-1 font-mono" data-testid="ew-price" /></div>
              <div><Label>HSN</Label><Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} className="rounded-[2px] mt-1 font-mono" /></div>
              <div><Label>GST %</Label><Input type="number" step="0.1" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} className="rounded-[2px] mt-1 font-mono" /></div>
            </div>
            <Button onClick={save} className="w-full rounded-[2px] bg-accent hover:bg-accent/90 text-white" data-testid="ew-save">Save</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
